import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { formatStartsIn, formatTimer } from '../hooks/useCountdown';
import { formatElapsedShort } from '../lib/format';

type Props = {
  secondsLeft: number;
  timerEndsAt?: number;
  // Auction end timestamp (ms) for the 'closed' variant — rendered as a compact
  // "time since close" token in the centre where live/upcoming show a countdown.
  closedAt?: number;
  size?: number;
  stroke?: number;
  scaleSec?: number;
  flashTrigger?: number;
  showLabel?: boolean;
  label?: string;
  // 'live' depletes the ring per-frame and escalates colour as it runs out.
  // 'upcoming' keeps the ring full + breathing in calm cyan (a lot awaiting its
  // start), rendering a coarse "10d 10h" / "23h 59m" / "59:59" countdown instead.
  // 'closed' is terminal: a static full ring in cooled-down steel cyan, no
  // motion — with a "CLOSED" label and a compact "time since close" token (in
  // bright cyan, e.g. "5m" / "2h" / "4d") in place of the countdown.
  variant?: 'live' | 'upcoming' | 'closed';
};

type State = 'idle' | 'warn' | 'danger';

// Thresholds match LotMainButton so the timer and BID button shift colour/pulse
// at the same instant: <=3s critical (red + pulse), <=7s urgent (yellow), else idle.
function pickState(s: number): State {
  if (s <= 3) return 'danger';
  if (s <= 7) return 'warn';
  return 'idle';
}

const STATE_COLOR: Record<State, string> = {
  idle: '#00e5ff',
  warn: '#ffd93d',
  danger: '#ff4d4d',
};

// Closed ring = the live cyan ring cooled down: a muted steel-cyan that stays in
// the timer's hue family but reads as settled/archived. The date text inside it
// stays bright cyan so the end date still pops.
const CLOSED_RING_COLOR = '#6f97a8';

export function RingTimer({
  secondsLeft,
  timerEndsAt,
  closedAt,
  size = 120,
  stroke = 6,
  scaleSec = 60,
  flashTrigger = 0,
  showLabel = true,
  label = 'TIMER',
  variant = 'live',
}: Props) {
  const upcoming = variant === 'upcoming';
  const closed = variant === 'closed';
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;

  // Dynamic ring scale: when timerEndsAt changes (new bid extends the timer),
  // snapshot the remaining duration as the new "100% of the ring". This way each
  // extension restarts the ring from full instead of jumping mid-arc to whatever
  // fraction the new value happens to be of a fixed scale.
  const [scaleSecDyn, setScaleSecDyn] = useState<number>(() =>
    timerEndsAt != null ? Math.max(0.1, (timerEndsAt - Date.now()) / 1000) : scaleSec,
  );
  useEffect(() => {
    if (timerEndsAt == null) {
      setScaleSecDyn(scaleSec);
      return;
    }
    setScaleSecDyn(Math.max(0.1, (timerEndsAt - Date.now()) / 1000));
  }, [timerEndsAt, scaleSec]);

  // The depleting arc is the only thing that needs sub-second motion. Driving it
  // through React state (a setState every rAF) re-rendered this whole SVG — blur
  // filter included — 60×/sec, and a live home feed mounts ~a dozen of these at
  // once, so on a CPU-bound device (mid-range phone in the TG WebView) the feed
  // collapsed to ~10fps (measured via Long Animation Frames under 6× throttle).
  // Instead we mutate stroke-dashoffset on the circle node directly inside the
  // loop: zero re-renders between ticks. Colour and the MM:SS text still derive
  // from the integer `secondsLeft` prop, which changes ~1×/sec, so React commits
  // at that rate — not per frame.
  const progressCircleRef = useRef<SVGCircleElement>(null);
  useEffect(() => {
    if (upcoming || closed || timerEndsAt == null) return;
    let raf = 0;
    const tick = () => {
      const el = progressCircleRef.current;
      if (el) {
        const remaining = Math.max(0, (timerEndsAt - Date.now()) / 1000);
        const p = scaleSecDyn > 0 ? Math.min(1, remaining / scaleSecDyn) : 0;
        el.setAttribute('stroke-dashoffset', String(c * (1 - p)));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [upcoming, closed, timerEndsAt, scaleSecDyn, c]);

  // Quantised offset for the first paint and the ~1/sec re-renders; the rAF loop
  // refines it between whole seconds. Re-mount on flash (svg key) also lands here
  // with a fresh value, so the new circle node never shows a stale arc.
  const quantProgress =
    upcoming || closed ? 1 : scaleSecDyn > 0 ? Math.min(1, secondsLeft / scaleSecDyn) : 0;
  const dashOffset = c * (1 - quantProgress);

  const state: State = upcoming || closed ? 'idle' : pickState(secondsLeft);
  const ringColor = closed ? CLOSED_RING_COLOR : STATE_COLOR[state];
  const textColor = closed ? STATE_COLOR.idle : ringColor;

  const closedDate = closedAt ?? timerEndsAt;
  const text = closed
    ? closedDate != null
      ? `${formatElapsedShort(closedDate)} ago`
      : 'CLOSED'
    : upcoming
      ? formatStartsIn(secondsLeft)
      : formatTimer(secondsLeft);

  const wrapRef = useRef<HTMLDivElement>(null);
  const [flashKey, setFlashKey] = useState(0);

  useEffect(() => {
    if (flashTrigger > 0) setFlashKey((k) => k + 1);
  }, [flashTrigger]);

  const innerSafe = Math.max(0, (radius - stroke - 4) * 2);
  const maxByWidth = innerSafe / (Math.max(5, text.length) * 0.58);
  // Closed shows a short "time since" token ("6m"); cap it lower than the live
  // timer so a 2-char value doesn't balloon to the loud MM:SS size — it should
  // read at the calm scale of the upcoming medallion's countdown.
  const maxFontSize = closed ? size * 0.19 : size * 0.26;
  const fontSize = Math.max(9, Math.min(maxFontSize, maxByWidth));
  const labelSize = Math.max(7, fontSize * 0.34);

  return (
    <div
      ref={wrapRef}
      className={`relative inline-flex items-center justify-center ${!upcoming && state === 'danger' ? 'anim-pulse-danger' : ''}`}
      style={
        {
          width: size,
          height: size,
          '--ring-flash-color': ringColor,
          '--ring-flash-glow': `${ringColor}99`,
        } as CSSProperties
      }
    >
      <svg
        key={flashKey}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="anim-flash"
        style={{ transform: 'rotate(-90deg)' }}
        aria-hidden="true"
      >
        <defs>
          <filter id={`glow-${size}-${state}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#1c2330"
          strokeWidth={stroke}
          fill="none"
        />

        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius - stroke - 2}
          stroke="#11161f"
          strokeWidth="1"
          fill="rgba(5, 8, 16, 0.55)"
        />

        <circle
          ref={progressCircleRef}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          filter={`url(#glow-${size}-${state})`}
          className={upcoming ? 'anim-glow' : undefined}
          style={{
            transition: 'stroke 0.3s ease',
          }}
        />

        {[0, 90, 180, 270].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const cx = size / 2 + (radius + stroke / 2 + 4) * Math.cos(rad);
          const cy = size / 2 + (radius + stroke / 2 + 4) * Math.sin(rad);
          return <circle key={deg} cx={cx} cy={cy} r={1.2} fill={ringColor} fillOpacity="0.55" />;
        })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span
          className="font-mono font-semibold tabular-nums tracking-tight"
          style={{
            fontSize,
            color: textColor,
            textShadow: `0 0 ${size * 0.06}px ${textColor}88`,
            lineHeight: 1,
          }}
        >
          {text}
        </span>
        {showLabel && (
          <span
            className="font-mono uppercase tracking-[0.25em] mt-1"
            style={{ fontSize: labelSize, color: '#7a8595' }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}
